import { z } from 'zod';
import { IdSchema, NameSchema } from '../resource-base/baseSchemas';
import { msObject } from '../resource-base/msObject';
import {
    DependencyValidationResultSchema,
    MutationValidationResourceSchema,
} from '../resources/validation/mutationValidationResource';

export const BackgroundTaskLogEntryResourceSchema = msObject({
    message: z.string(),
    timestamp: z.string(),
});

export type BackgroundTaskLogEntryResource = z.infer<typeof BackgroundTaskLogEntryResourceSchema>;

const BackgroundTaskResultSchema = msObject({
    successful: z.boolean(),
    failureReason: z.string().nullable(),
    failureMessage: z.string().nullable(),
    downloaded: z.boolean().optional(),
});

const BackgroundNormalTaskResultSchema = BackgroundTaskResultSchema.extend({
    type: z.literal('NORMAL'),
});

export type BackgroundNormalTaskResult = z.infer<typeof BackgroundNormalTaskResultSchema>;

export const ArtifactsCreatedStatisticsSchema = msObject({
    total: z.number(),
    notes: z.number(),
    warnings: z.number(),
    criticals: z.number(),
    fatal: z.number(),
});

const BackgroundImportTaskResultSchema = BackgroundTaskResultSchema.extend({
    type: z.literal('IMPORT'),
    erroneousArtifactType: z.string().nullable(),
    erroneousCreationResult: MutationValidationResourceSchema.nullable(),
    numberOfArtifactsCreated: z.number(),
    artifactsCreatedCountMap: z.record(z.string(), ArtifactsCreatedStatisticsSchema),
});

export type BackgroundImportTaskResult = z.infer<typeof BackgroundImportTaskResultSchema>;

export const ValidationResultSetSchema = z.record(
    z.string(),
    z.record(z.string(), z.record(z.string(), DependencyValidationResultSchema))
);

export type ValidationResultSet = z.infer<typeof ValidationResultSetSchema>;

const BackgroundValidatedExportTaskResultSchema = BackgroundTaskResultSchema.extend({
    type: z.literal('VALIDATED_EXPORT'),
    exceptionMessages: z.string().array().nullable(),
    validationResultSet: ValidationResultSetSchema.nullable(),
});

export type BackgroundValidatedExportTaskResult = z.infer<typeof BackgroundValidatedExportTaskResultSchema>;

const BackgroundExportTaskResultSchema = BackgroundTaskResultSchema.extend({
    type: z.literal('EXPORT'),
});

export type BackgroundExportTaskResult = z.infer<typeof BackgroundExportTaskResultSchema>;

export const BackgroundTaskResourceSchema = msObject({
    id: IdSchema,
    name: NameSchema,
    phase: z.string(),
    category: z.string(),
    properties: z.any(),
    user: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    elapsedTime: z.number(),
    isDone: z.boolean(),
    isCancelled: z.boolean(),
    isRunning: z.boolean(),
    lastLogEntry: BackgroundTaskLogEntryResourceSchema.nullable(),
    result: z
        .union([
            BackgroundNormalTaskResultSchema,
            BackgroundImportTaskResultSchema,
            BackgroundValidatedExportTaskResultSchema,
            BackgroundExportTaskResultSchema,
        ])
        .nullable(),
});

export type BackgroundTaskResource = z.infer<typeof BackgroundTaskResourceSchema>;
